/**
 * The nav glyph set.
 *
 * Icons, not a primitive: the seller portal ships its own sprite and the two
 * apps navigate to different places, so there is nothing to share here beyond
 * the row styling that already lives in `@a2z/ui`'s `navRowClass`. What matters
 * is that every row HAS one — a nav list of bare labels reads as a different
 * product next to one where each row carries a mark, and the eye re-finds a
 * shape faster than it re-reads a word mid-service.
 *
 * All drawn on the same 24px grid at the same 1.7 stroke so they sit evenly in
 * a column; `currentColor` so they inherit the row's active/muted ink.
 */
const base = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export const NavIcon = {
  /** Today — the back office's home (D111), drawn as a house. */
  today: () => (
    <svg {...base}>
      <path d="M3.5 10.6 12 3.6l8.5 7" />
      <path d="M5.6 9v11.4h12.8V9" />
      <path d="M10 20.4v-5.6h4v5.6" />
    </svg>
  ),
  /** Tables — the floor grid the POS opens on. */
  pos: () => (
    <svg {...base}>
      <rect x="3" y="3" width="7" height="7" rx="1.6" />
      <rect x="14" y="3" width="7" height="7" rx="1.6" />
      <rect x="3" y="14" width="7" height="7" rx="1.6" />
      <rect x="14" y="14" width="7" height="7" rx="1.6" />
    </svg>
  ),
  /** Menu — a served plate. */
  menu: () => (
    <svg {...base}>
      <path d="M3 11h18" />
      <path d="M5 11a7 7 0 0 1 14 0" />
      <path d="M4 15h16" />
    </svg>
  ),
  /** Floor — the room plan. */
  floor: () => (
    <svg {...base}>
      <path d="M3 21V6l6-3 6 3 6-3v15l-6 3-6-3-6 3Z" />
      <path d="M9 3v15M15 6v15" />
    </svg>
  ),
  /** Addons — a modifier stacked on an item. */
  addons: () => (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 8.4v7.2M8.4 12h7.2" />
    </svg>
  ),
  /** Categories — the folder the tiles already use. */
  categories: () => (
    <svg {...base}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  /** Ingredients — stock on a shelf. */
  ingredients: () => (
    <svg {...base}>
      <path d="M3 8.5 12 4l9 4.5-9 4.5-9-4.5Z" />
      <path d="M3 15.5 12 20l9-4.5" />
      <path d="M3 12 12 16.5 21 12" />
    </svg>
  ),
  /** Receive a delivery (D147) — a box with an arrow going in. */
  receiveDelivery: () => (
    <svg {...base}>
      <path d="M3.5 9.5 12 5l8.5 4.5v8.5L12 22.5 3.5 18z" />
      <path d="M12 13.5v-6" />
      <path d="m9.4 10.9 2.6 2.6 2.6-2.6" />
    </svg>
  ),
  /** Stocktake — a clipboard with a tick: the shelf counted against the books. */
  stocktake: () => (
    <svg {...base}>
      <path d="M8.5 4.5H6.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-13a2 2 0 0 0-2-2h-2" />
      <rect x="8.5" y="2.8" width="7" height="3.4" rx="1" />
      <path d="m9 13.5 2.2 2.2 4-4.2" />
    </svg>
  ),
  /** Suppliers — a delivery van. Deliberately not a second box: `ingredients`
   *  is already a stack on a shelf, and a supplier is who BRINGS it. */
  suppliers: () => (
    <svg {...base}>
      <path d="M2.5 6.5h11v9h-11z" />
      <path d="M13.5 9.5h3.6l2.9 3.2v2.8h-6.5z" />
      <circle cx="6.6" cy="17.6" r="1.9" />
      <circle cx="16.4" cy="17.6" r="1.9" />
    </svg>
  ),
  /** Invoices — a receipt with a torn edge. */
  invoices: () => (
    <svg {...base}>
      <path d="M6 2.5h12v19l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4Z" />
      <path d="M9.5 8h5M9.5 12h5" />
    </svg>
  ),
  /** Kitchen — a pan over a flame, the one glyph nobody confuses with a plate.
   *  `menu` is already a served plate, so the kitchen needs the cooking side of
   *  the same object rather than a second dome. */
  kitchen: () => (
    <svg {...base}>
      <path d="M3.5 9.5h13v4.5a4 4 0 0 1-4 4h-5a4 4 0 0 1-4-4Z" />
      <path d="M16.5 10.8h2.2a2 2 0 0 1 0 4h-2.2" />
      <path d="M7 6.2c0-1 1-1.4 1-2.4M10.5 6.2c0-1 1-1.4 1-2.4" />
    </svg>
  ),
  /** Settings. */
  settings: () => (
    <svg {...base}>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 2.7 15a1.7 1.7 0 0 0-1.6-1H1a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 2.7 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 7 4.6h.08A1.7 1.7 0 0 0 8.1 3V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.55" />
    </svg>
  ),
  /** Staff. */
  staff: () => (
    <svg {...base}>
      <circle cx="9" cy="8" r="3.3" />
      <path d="M2.6 20a6.4 6.4 0 0 1 12.8 0" />
      <path d="M16.5 5.2a3.3 3.3 0 0 1 0 6.4M17.5 14.4A6.4 6.4 0 0 1 21.4 20" />
    </svg>
  ),
  /** Reports — a rising bar chart, the spent-vs-gained report's own glyph. */
  reports: () => (
    <svg {...base}>
      <path d="M4 20V10M11 20V4M18 20v-7" />
      <path d="M2.5 20h19" />
    </svg>
  ),
  /** Customers — a person on a contact card, the customer list's own glyph. */
  customers: () => (
    <svg {...base}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9.5" cy="10.5" r="2.5" />
      <path d="M5.6 17a4 4 0 0 1 7.8 0" />
      <path d="M15.5 9.5h3M15.5 13h3" />
    </svg>
  ),
  /** Shift — a cash drawer, drawn open. The live shift screen's own glyph. */
  shift: () => (
    <svg {...base}>
      <rect x="2.5" y="8.5" width="19" height="12" rx="1.6" />
      <path d="M2.5 13.2h19" />
      <path d="M10 13.2v2.4h4v-2.4" />
      <path d="M6 8.5 8.3 4h7.4l2.3 4.5" />
    </svg>
  ),
  /** Shift history — a clock over a short list, the closed-shift review. */
  shiftHistory: () => (
    <svg {...base}>
      <circle cx="9.3" cy="9.3" r="6.3" />
      <path d="M9.3 6v3.3l2.3 1.3" />
      <path d="M15 16.5h6M15 20h6M18.6 13h2.4" />
    </svg>
  ),
} as const;

export type NavIconKey = keyof typeof NavIcon;
